import { register, run } from "./registry";

// Report runner (WP50): after each run, the filters that differ from their defaults show as small chips above the grid.
// A chip's x puts that filter back to its default and runs the report again. Dates are left out: the scope line shows the period.

type Active = { label: string; text: string; reset: () => void };

function activeFilters(form: HTMLFormElement): Active[] {
  const out: Active[] = [];
  form.querySelectorAll<HTMLLabelElement>("label").forEach((label) => {
    const control = label.querySelector<HTMLInputElement | HTMLSelectElement>("select, input:not([type=hidden])");
    if (!control || control.matches("[data-kit-miti], [name=page_size]")) return;
    const name = (label.firstChild?.textContent ?? "").trim();
    if (control instanceof HTMLSelectElement) {
      const options = Array.from(control.options);
      const first = Math.max(0, options.findIndex((o) => o.defaultSelected));
      if (control.selectedIndex < 0 || control.selectedIndex === first) return;
      out.push({ label: name, text: control.selectedOptions[0]?.text ?? "", reset: () => { control.selectedIndex = first; control.dispatchEvent(new Event("change", { bubbles: true })); } });
      return;
    }
    const target = control.dataset.kitAcTarget;
    const hidden = target ? form.querySelector<HTMLInputElement>(`input[type=hidden][name="${target}"]`) : null;
    if (hidden) {
      if (!hidden.value) return;
      out.push({ label: name, text: control.value, reset: () => { control.value = ""; hidden.value = ""; } });
      return;
    }
    const value = control.value.trim();
    if (!value || value === control.defaultValue.trim()) return;
    out.push({ label: name, text: value, reset: () => { control.value = control.defaultValue; } });
  });
  return out;
}

function renderChips(form: HTMLFormElement, host: HTMLElement): void {
  const sprite = document.querySelector("svg.icon use")?.getAttribute("href")?.split("#")[0] ?? "/icons/sprite.svg";
  host.replaceChildren();
  for (const f of activeFilters(form)) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "report-chip";
    chip.title = `Clear ${f.label}`;
    chip.innerHTML = `<span></span><strong></strong><svg class="icon" aria-hidden="true"><use href="${sprite}#x"></use></svg>`;
    chip.querySelector("span")!.textContent = f.label;
    chip.querySelector("strong")!.textContent = f.text;
    chip.addEventListener("click", () => { f.reset(); form.requestSubmit(); });
    host.append(chip);
  }
}

register((root) => {
  const grid = document.getElementById("report-grid");
  const form = document.getElementById("report-form") as HTMLFormElement | null;
  const host = document.getElementById("report-chips");
  if (!grid || !form || !host) return;
  if (root === grid || (root instanceof Node && root.contains(grid) && grid.childElementCount > 0)) renderChips(form, host);
});

// The grid handler answers a refused run (a required filter, an unknown series) with 4xx and its own message; htmx drops 4xx
// answers by default, so Show looked dead. Swap the message into the grid instead.
document.body.addEventListener("htmx:beforeSwap", (e: Event) => {
  const d = (e as CustomEvent).detail as { xhr: XMLHttpRequest; target: Element; shouldSwap: boolean; isError: boolean };
  if (d.target?.id === "report-grid" && d.xhr.status >= 400 && d.xhr.status < 500) {
    d.shouldSwap = true;
    d.isError = false;
  }
});

// Keep the working report when Back cannot use the browser's in-memory page cache (and on reload).
// The user ID namespaces both the rows and the autocomplete labels; another login never restores them.
register((root) => {
  if (root !== document) return;
  const form = document.querySelector<HTMLFormElement>("#report-form[data-report-user]");
  const grid = document.getElementById("report-grid");
  const chips = document.getElementById("report-chips");
  if (!form || !grid || !chips) return;
  // Pager buttons sit outside the form and arrive in swaps. Use the native submitter even before htmx has settled/bound it.
  let requestedPage = "1";
  form.addEventListener("submit", (e) => {
    const button = (e as SubmitEvent).submitter;
    requestedPage = button instanceof HTMLButtonElement && button.name === "page" ? button.value : "1";
  }, true);
  form.addEventListener("htmx:configRequest", (e: Event) => {
    if (e.target !== form) return;
    (e as CustomEvent<{ parameters: Record<string, string> }>).detail.parameters.page = requestedPage;
  });
  const key = `report:${form.dataset.reportUser}:${location.pathname}${location.search}`;
  const controls = () => Array.from(form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input, select"));
  const controlKey = (c: HTMLInputElement | HTMLSelectElement) => c.name || `text:${c.dataset.kitAcTarget ?? ""}`;
  type Snapshot = { fields: Record<string, string>; html: string; more: boolean; x: number; y: number; tableX: number; tableY: number; exports: Record<string, string> };
  const save = () => {
    if (!grid.querySelector("[data-report-grid]")) return;
    const scroll = grid.querySelector<HTMLElement>(".kit-report-scroll");
    const snapshot: Snapshot = {
      fields: Object.fromEntries(controls().map(c => [controlKey(c), c.value])),
      html: grid.innerHTML, more: form.classList.contains("is-open"), x: scrollX, y: scrollY,
      tableX: scroll?.scrollLeft ?? 0, tableY: scroll?.scrollTop ?? 0,
      exports: Object.fromEntries(Array.from(document.querySelectorAll<HTMLAnchorElement>("a[data-report-export]")).map(a => [a.id, a.href])),
    };
    try { sessionStorage.setItem(key, JSON.stringify(snapshot)); } catch { /* the current report still works when storage is unavailable */ }
  };
  window.addEventListener("pagehide", save);
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (navigation?.type === "back_forward" || navigation?.type === "reload") {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const snapshot = JSON.parse(raw) as Snapshot;
        for (const c of controls()) if (snapshot.fields[controlKey(c)] !== undefined) {
          c.value = snapshot.fields[controlKey(c)]!;
          if (c.matches("[data-kit-miti]")) c.dispatchEvent(new Event("input")); // refresh AD hints without running the report
        }
        form.classList.toggle("is-open", snapshot.more);
        form.querySelector(".list-more-toggle")?.setAttribute("aria-expanded", String(snapshot.more));
        grid.innerHTML = snapshot.html;
        for (const [id, href] of Object.entries(snapshot.exports)) document.getElementById(id)?.setAttribute("href", href);
        renderChips(form, chips);
        const htmx = (window as unknown as { htmx?: { process: (el: Element) => void } }).htmx;
        htmx?.process(grid);
        run(grid);
        requestAnimationFrame(() => {
          const scroll = grid.querySelector<HTMLElement>(".kit-report-scroll");
          if (scroll) { scroll.scrollLeft = snapshot.tableX; scroll.scrollTop = snapshot.tableY; }
          window.scrollTo(snapshot.x, snapshot.y);
        });
        return;
      }
    } catch { sessionStorage.removeItem(key); }
  }
  // Drill links carry filters in the URL. Apply those values before running the report rather than displaying a blank form.
  const query = new URLSearchParams(location.search);
  let explicit = false;
  for (const c of controls()) {
    if (c.name && query.has(c.name)) { c.value = query.get(c.name)!; explicit = true; }
  }
  if (explicit) form.requestSubmit();
});
